import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Copy, Type, FileQuestion, Sparkles, ChevronDown, ChevronRight, Search, CheckCircle2, X } from 'lucide-react';
import { Card } from '../../../../components/ui/Card';
import { Input } from '../../../../components/ui/Input';
import { Badge } from '../../../../components/ui/Badge';
import { validateHintQuality, type HintQualityIssue, type HintQualityReport } from '../../../../support';
// F-H6 (19 May 2026 — finalised): widget accepts a pre-computed `report` prop
// from SupportContentPage. Previously the page called `validateHintQuality()`
// for its own stats AND the widget re-ran it internally on every render —
// same 260-entry scan twice per page load. The fallback (no prop) keeps the
// widget usable in isolation (e.g. Storybook / future standalone admin pane).
// NOTE: a comment claiming this fix existed since 17 May 2026 was present
// before, but the prop was never actually wired into the interface. Audit
// §20.2 (ADMIN_DOMAIN_RC_AUDIT_2026 v4) caught this and the fix is now real.
/**
 * Admin widget for `validateHintQuality()` — surfaces hint authoring issues
 * (too-long sentences, jargon, cross-family duplicates, empties) directly on
 * SupportContentPage so editors can see and act on them during audit work.
 *
 * Co-located under portals/admin/content/support/ to mirror the structure used
 * by content/governance/ and content/questionBank/.
 */
type IssueKind = HintQualityIssue['kind'];
const KIND_META: Record<IssueKind, {
  label: string;
  icon: React.ReactNode;
  color: string;
  bg: string;
  border: string;
  description: string;
}> = {
  length: {
    label: 'Te lang',
    icon: <Type className="w-3.5 h-3.5" data-id="element-6323" data-mp="22xragdb8eqii" />,
    color: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    description: 'Zinnen die langer zijn dan 14 woorden — moeilijk leesbaar voor 9-12 jr.'
  },
  jargon: {
    label: 'Jargon',
    icon: <FileQuestion className="w-3.5 h-3.5" data-id="element-6324" data-mp="19xui7j6yci5z" />,
    color: 'text-rose-700',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    description: 'Academische termen die kinderen waarschijnlijk niet kennen.'
  },
  duplicate: {
    label: 'Dubbel',
    icon: <Copy className="w-3.5 h-3.5" data-id="element-6325" data-mp="v86hok6lhnjb" />,
    color: 'text-violet-700',
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    description: 'Identieke hint gebruikt in meerdere families — context-specifieker maken.'
  },
  empty: {
    label: 'Leeg',
    icon: <X className="w-3.5 h-3.5" data-id="element-6326" data-mp="1nxlqd3asf4c2" />,
    color: 'text-slate-700',
    bg: 'bg-slate-100',
    border: 'border-slate-300',
    description: 'Hint zonder content.'
  }
};
interface HintQualityWidgetProps {
  /**
   * Pre-computed hint-quality report. When provided, the widget skips its
   * own internal scan — this is the preferred path because SupportContentPage
   * already runs validateHintQuality() for its own stats. Without this prop
   * the widget falls back to its own scan so it stays usable in isolation
   * (Storybook, future standalone admin pane).
   */
  report?: HintQualityReport;
  /** Optional click handler to jump to a family's preview pane. */
  onSelectFamily?: (familyId: string) => void;
}
export function HintQualityWidget({
  report: providedReport,
  onSelectFamily
}: HintQualityWidgetProps) {
  const [expanded, setExpanded] = useState(true);
  const [activeKinds, setActiveKinds] = useState<Set<IssueKind>>(new Set(['length', 'jargon', 'duplicate', 'empty']));
  const [search, setSearch] = useState('');
  // Only run the internal scan when no report is provided. useMemo is always
  // called (hooks rule) but its body short-circuits to null when a report
  // comes in via props — so we pay the 260-entry scan at most once, and only
  // when running standalone.
  const fallbackReport = useMemo<HintQualityReport | null>(() => providedReport ? null : validateHintQuality(), [providedReport]);
  const report: HintQualityReport = providedReport ?? fallbackReport!;
  const totalIssues = report.issues.length;
  const healthyHints = Math.max(0, report.totalHints - totalIssues);
  const healthPct = report.totalHints > 0 ? Math.round(healthyHints / report.totalHints * 100) : 100;
  const filteredIssues = useMemo(() => {
    return report.issues.filter(issue => {
      if (!activeKinds.has(issue.kind)) return false;
      if (search && !issue.familyId.toLowerCase().includes(search.toLowerCase()) && !issue.message.toLowerCase().includes(search.toLowerCase()) && !(issue.snippet ?? '').toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [report.issues, activeKinds, search]);
  const toggleKind = (kind: IssueKind) => {
    setActiveKinds(prev => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);else next.add(kind);
      // never empty — re-enable all if user toggles last one off
      if (next.size === 0) return new Set(['length', 'jargon', 'duplicate', 'empty']);
      return next;
    });
  };
  const headerColor = totalIssues === 0 ? 'text-emerald-700' : totalIssues < 20 ? 'text-amber-700' : 'text-rose-700';
  return <Card className="mb-6 border-gray-200" data-id="element-6327" data-mp="1mdsixyw9l5zs">
      {/* Header — always visible */}
      <button type="button" onClick={() => setExpanded(e => !e)} className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50/60 transition-colors text-left" aria-expanded={expanded} aria-controls="hint-quality-panel" data-id="element-6328" data-mp="2a4fucu5e2dr3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${totalIssues === 0 ? 'bg-emerald-100' : 'bg-amber-100'}`} data-id="element-6329" data-mp="208crfm2pqb33">
          {totalIssues === 0 ? <CheckCircle2 className="w-5 h-5 text-emerald-600" data-id="element-6330" data-mp="ot66k7ysyki4" /> : <AlertTriangle className="w-5 h-5 text-amber-600" data-id="element-6331" data-mp="tntofnzawgnq" />}
        </div>

        <div className="flex-1 min-w-0" data-id="element-6332" data-mp="2wbsa3xvunq5h">
          <div className="flex items-center gap-2 flex-wrap" data-id="element-6333" data-mp="2rdqszihzi0wn">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-6334" data-mp="2rea7ep8fe75x">
              Hint-kwaliteit
            </h3>
            <span className={`text-xs font-medium ${headerColor}`} data-id="element-6335" data-mp="2mkfhh4288vye">
              {totalIssues === 0 ? 'Alle hints schoon ✨' : `${totalIssues} ${totalIssues === 1 ? 'issue' : 'issues'} in ${report.familiesChecked} families`}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5" data-id="element-6336" data-mp="28p3n4xrzh7i0">
            {report.totalHints} hints gescand · {healthyHints} schoon (
            {healthPct}%)
          </p>
        </div>

        {/* Mini kind summary inline */}
        <div className="hidden md:flex items-center gap-1.5 flex-shrink-0" data-id="element-6337" data-mp="1wjag9nvi0fa">
          {(Object.keys(KIND_META) as IssueKind[]).map(kind => {
          const count = report.summary[kind];
          if (count === 0) return null;
          const meta = KIND_META[kind];
          return <span key={kind} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${meta.bg} ${meta.color} ${meta.border}`} data-id="element-6338" data-mp="9g2c5x4wcid7">
                {meta.icon}
                <span data-id="element-6339" data-mp="j4kam7ppeddo">{count}</span>
              </span>;
        })}
        </div>

        <div className="flex-shrink-0 text-gray-400" data-id="element-6340" data-mp="3hoke722eltcf">
          {expanded ? <ChevronDown className="w-4 h-4" data-id="element-6341" data-mp="3mlsoyhl82v3n" /> : <ChevronRight className="w-4 h-4" data-id="element-6342" data-mp="3mof6na1tuqzl" />}
        </div>
      </button>

      {/* Expandable panel */}
      <AnimatePresence initial={false} data-id="element-6343" data-mp="f48xh2a8evo">
        {expanded && <motion.div id="hint-quality-panel" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden border-t border-gray-100" data-id="element-6344" data-mp="daydqojtaie">
            <div className="p-5 space-y-4" data-id="element-6345" data-mp="26p6fr4cc8crl">
              {/* Health bar */}
              <div data-id="element-6346" data-mp="21xekwhm5fkvn">
                <div className="flex items-center justify-between mb-1.5 text-xs text-gray-600 font-medium" data-id="element-6347" data-mp="21wwl5b0a2oiz">
                  <span data-id="element-6348" data-mp="2fni5z59al8ji">Gezonde hints</span>
                  <span data-id="element-6349" data-mp="2bnrfz3rlawg1">
                    {healthyHints} / {report.totalHints}
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-6350" data-mp="2pm7lwshmoix4">
                  <motion.div initial={{
                width: 0
              }} animate={{
                width: `${healthPct}%`
              }} transition={{
                duration: 0.5,
                ease: 'easeOut'
              }} className={`h-full rounded-full ${healthPct === 100 ? 'bg-emerald-500' : healthPct >= 80 ? 'bg-amber-400' : 'bg-rose-400'}`} data-id="element-6351" data-mp="2kno14vertd6c" />
                </div>
              </div>

              {totalIssues === 0 ? <div className="flex items-center gap-3 p-4 rounded-lg bg-emerald-50/60 border border-emerald-100" data-id="element-6352" data-mp="2x6i5yp38n23d">
                  <Sparkles className="w-5 h-5 text-emerald-500 flex-shrink-0" data-id="element-6353" data-mp="2j6qx12rkwbw8" />
                  <div data-id="element-6354" data-mp="2o5tr5s60sxpf">
                    <p className="text-sm font-medium text-emerald-900" data-id="element-6355" data-mp="2o74vbt3do7h2">
                      Alle hints voldoen aan de kwaliteitscriteria.
                    </p>
                    <p className="text-xs text-emerald-700/80 mt-0.5" data-id="element-6356" data-mp="2e7ntjmr8ydqd">
                      Lengte ≤ 14 woorden per zin · geen jargon · geen
                      cross-family duplicaten · geen leegtes.
                    </p>
                  </div>
                </div> : <>
                  {/* Filters */}
                  <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center" data-id="element-6357" data-mp="3rwn7r95eg2aw">
                    <div className="flex-1 min-w-0" data-id="element-6358" data-mp="jizdurrmzof">
                      <Input type="text" placeholder="Zoek in family, bericht of zin..." value={search} onChange={e => setSearch(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-6360" data-mp="ej1wlsj1gqly" />} variant="admin" size="sm" data-id="element-6359" data-mp="3hzhg9zsvi9a5" />
                    </div>
                    <div className="flex flex-wrap gap-1.5" data-id="element-6361" data-mp="4jb39u67shng">
                      {(Object.keys(KIND_META) as IssueKind[]).map(kind => {
                  const meta = KIND_META[kind];
                  const count = report.summary[kind];
                  const isActive = activeKinds.has(kind);
                  return <button key={kind} type="button" onClick={() => toggleKind(kind)} disabled={count === 0} title={meta.description} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-all ${count === 0 ? 'opacity-40 cursor-not-allowed bg-gray-50 text-gray-400 border-gray-200' : isActive ? `${meta.bg} ${meta.color} ${meta.border} shadow-sm` : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`} data-id="element-6362" data-mp="2u7aegl4dpa3c">
                            {meta.icon}
                            <span data-id="element-6363" data-mp="xn5gzgkjntn1">{meta.label}</span>
                            <span className={`tabular-nums ${isActive && count > 0 ? '' : 'opacity-60'}`} data-id="element-6364" data-mp="xmc746qago0o">
                              {count}
                            </span>
                          </button>;
                })}
                    </div>
                  </div>

                  {/* Issue list */}
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-[420px] overflow-y-auto bg-white" data-id="element-6365" data-mp="29y7x8r4sly04">
                    {filteredIssues.length === 0 ? <div className="p-6 text-center text-sm text-gray-400" data-id="element-6366" data-mp="2joaq8rbwzbyg">
                        Geen issues met deze filters.
                      </div> : filteredIssues.map((issue, i) => {
                const meta = KIND_META[issue.kind];
                const clickable = !!onSelectFamily;
                return <div key={`${issue.familyId}-${issue.hintIndex}-${issue.kind}-${i}`} className={`flex items-start gap-3 px-3 py-2.5 ${clickable ? 'cursor-pointer hover:bg-gray-50' : ''}`} onClick={clickable ? () => onSelectFamily!(issue.familyId) : undefined} role={clickable ? 'button' : undefined} tabIndex={clickable ? 0 : undefined} onKeyDown={clickable ? e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectFamily!(issue.familyId);
                  }
                } : undefined} data-id="element-6367" data-mp="20zvwvxe4jluh" data-mpi={i}>
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border flex-shrink-0 mt-0.5 ${meta.bg} ${meta.color} ${meta.border}`} data-id="element-6368" data-mp="22fi4mba7ahni">
                              {meta.icon}
                              {meta.label}
                            </span>
                            <div className="flex-1 min-w-0" data-id="element-6369" data-mp="l5cr1acxgrjc">
                              <div className="flex items-center gap-2 flex-wrap" data-id="element-6370" data-mp="q3wddbvfo5c8">
                                <span className="font-mono text-xs font-semibold text-gray-700" data-id="element-6371" data-mp="bg1ibotydovz">
                                  {issue.familyId}
                                </span>
                                <Badge variant="secondary" data-id="element-6372" data-mp="2f3hffet9xhm">
                                  hint #{issue.hintIndex + 1}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-700 mt-0.5" data-id="element-6373" data-mp="3ombdts7r6u1i">
                                {issue.message}
                              </p>
                              {issue.snippet && <p className="text-xs text-gray-500 italic mt-1 line-clamp-2" data-id="element-6374" data-mp="39zcoq3wdcbpy">
                                  "{issue.snippet}"
                                </p>}
                            </div>
                          </div>;
              })}
                  </div>

                  {filteredIssues.length > 0 && <p className="text-xs text-gray-400" data-id="element-6375" data-mp="3sxp07war5xop">
                      {filteredIssues.length} van {totalIssues} issues —{' '}
                      {clickableHint(onSelectFamily)}
                    </p>}
                </>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </Card>;
}
function clickableHint(onSelectFamily?: (id: string) => void): string {
  return onSelectFamily ? 'klik op een rij om de family te openen.' : 'criteria: zinlengte ≤ 14 wrd · geen jargon · geen cross-family duplicaten.';
}